import React, { useState, useEffect } from 'react';
import { getPageContent, savePageContent, DEFAULT_PAGE_CONTENTS } from '../services/pageContentService';
import HtmlContent, { decodeHtmlEntities } from './HtmlContent';
import { isRawHtmlPage, getHtmlSections } from './RawHtmlPage';

// The three cards on the home page (tag shown above each heading on the live site)
const HOME_CARDS = [
  { n: 1, tag: 'Overview' },
  { n: 2, tag: 'Focus & Scope' },
  { n: 3, tag: 'Publisher' },
];

const PAGE_DEFINITIONS = [
  { key: 'home_cards', label: 'Home Page Cards', path: '/' },
  { key: 'about_us', label: 'About the Journal', path: '/about-us' },
  { key: 'aims_and_scope', label: 'Aim and Scope', path: '/aims-and-scope' },
  { key: 'author_guidelines', label: 'Author Guidelines', path: '/information-for-authors' },
  { key: 'peer_review', label: 'Peer Review Process', path: '/peer-review' },
  { key: 'publication_ethics', label: 'Publication Ethics', path: '/publication-ethics' },
  { key: 'contact', label: 'Contact Information', path: '/contact' },
  { key: 'editorial_board', label: 'Editorial Board', path: '/editorial-board' },
  { key: 'archives', label: 'Archives & Issues', path: '/archives' },
  { key: 'call_for_papers', label: 'Call for Papers', path: '/call-for-papers' },
];

/**
 * Compiles modular sections and custom head titles into a clean, unified HTML document.
 */
const generateFullPageHtml = (pageKey, data) => {
  const titles = data?.titles || {};

  if (pageKey === 'about_us') {
    const bullets = Array.isArray(data.mission_bullets) && data.mission_bullets.length > 0
      ? `<ul>\n${data.mission_bullets.map((b) => `  <li>${b}</li>`).join('\n')}\n</ul>\n`
      : '';

    return `<h2>${titles.section_2_1 || 'Mission, Vision & Scope'}</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<h3>${titles.mission || 'Mission Statement'}</h3>
<p>${data.mission || ''}</p>
${bullets}
<h3>${titles.vision || 'Vision Statement'}</h3>
<p>${data.vision || ''}</p>

<h3>${titles.scope || 'Scope Overview'}</h3>
<p>${data.scope || ''}</p>

<h3>${titles.editorial_policy || 'Editorial Policy'}</h3>
<p>${data.editorial_policy || ''}</p>

<h3>${titles.who_can_submit || 'Who Can Submit?'}</h3>
<p>${data.who_can_submit || ''}</p>

<h2>${titles.section_2_2 || 'Journal Information & Publication History'}</h2>
<p><strong>Journal Title:</strong> ${data.journal_title || 'Asian Journal of Multidisciplinary Research'}</p>
<p><strong>Publication Frequency:</strong> ${data.journal_frequency || 'Biannual (January–June, July–December)'}</p>
<p><strong>Publishing Model:</strong> ${data.journal_model || 'Open Access, Peer-Reviewed'}</p>
<p><strong>Publication History:</strong> ${data.journal_history || ''}</p>

<h2>${titles.section_2_3 || 'Publisher & Institutional Information'}</h2>
<p><strong>Publisher:</strong> ${data.publisher_name || ''}</p>
<p><strong>Registered Address:</strong> ${data.publisher_address || ''}</p>
<p><strong>Institutional Affiliation:</strong> ${data.publisher_affiliation || ''}</p>`;
  }

  if (pageKey === 'aims_and_scope') {
    const scopeItems = Array.isArray(data.scope_items) ? data.scope_items : [];
    const artTypes = Array.isArray(data.article_types) ? data.article_types : [];
    const audBullets = Array.isArray(data.target_audience_bullets) && data.target_audience_bullets.length > 0
      ? `<ul>\n${data.target_audience_bullets.map((b) => `  <li>${b}</li>`).join('\n')}\n</ul>\n`
      : '';

    return `<h2>${titles.aim || 'Principal Aim'}</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<p>${data.aim || ''}</p>

<h2>${titles.scope || 'Scope & Subject Areas'}</h2>
<ul>
${scopeItems.map((s) => `  <li>${s}</li>`).join('\n')}
</ul>

<h2>${titles.geographical_scope || 'Geographical Scope'}</h2>
<p>${data.geographical_scope || ''}</p>

<h2>${titles.target_audience || 'Target Audience'}</h2>
<p>${data.target_audience || ''}</p>
${audBullets}
<h2>${titles.editorial_scope || 'Editorial Scope and Peer Review Policy'}</h2>
<p>${data.editorial_scope || ''}</p>

<h2>${titles.article_types || 'Article Types Accepted'}</h2>
<ul>
${artTypes.map((a) => `  <li><strong>${a.title}:</strong> ${a.desc}</li>`).join('\n')}
</ul>`;
  }

  if (pageKey === 'author_guidelines') {
    return `<h2>${titles.manuscript_preparation || 'Manuscript Preparation'}</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<h3>${titles.sub_prep_overview || 'Overview Statement'}</h3>
<p>${data.manuscript_preparation || ''}</p>

<h3>${titles.sub_formatting || 'Formatting & Technical Specifications'}</h3>
<p><strong>Font:</strong> ${data.formatting_font || 'Times New Roman 12pt'}</p>
<p><strong>Spacing:</strong> ${data.formatting_spacing || '1.5 line spacing'}</p>
<p><strong>Nomenclature:</strong> ${data.formatting_remedies || 'Standard scientific nomenclature'}</p>

<h3>${titles.sub_structure || 'Manuscript Structure (IMRAD)'}</h3>
<p>${data.structure || ''}</p>

<h3>${titles.sub_conflict || 'Conflict of Interest Declaration'}</h3>
<p>${data.conflict_of_interest || ''}</p>

<h3>${titles.sub_funding || 'Funding Sources'}</h3>
<p>${data.funding || ''}</p>

<h3>${titles.sub_originality || 'Originality & Duplicate Submission Policy'}</h3>
<p>${data.originality || ''}</p>

<h3>${titles.sub_ethics || 'Ethical Approval & Patient Consent'}</h3>
<p>${data.ethics_and_consent || ''}</p>

<h3>${titles.sub_figures || 'Tables, Figures & Language Standards'}</h3>
<p>${data.tables_figures || ''}</p>

<h3>${titles.sub_responsibility || 'Author Responsibility'}</h3>
<p>${data.author_responsibility || ''}</p>

<h2>${titles.submission_process || 'Submission Process'}</h2>
<p>${data.submission_process || ''}</p>

<h2>${titles.apc || 'Article Processing Charges (APC)'}</h2>
<p>${data.apc_overview || ''}</p>
<p><strong>Fee Schedule:</strong> ${data.apc_fee || ''}</p>
<p><strong>Waiver Policy:</strong> ${data.waiver_policy || ''}</p>`;
  }

  if (pageKey === 'peer_review') {
    const stages = Array.isArray(data.stages) ? data.stages : [];
    const stagesHtml = stages
      .map((st) => `<h3>${(st.title || '').replace(/^[\d.]+\s*/, '')} (${st.timeframe || ''})</h3>\n<p>${st.desc || ''}</p>`)
      .join('\n\n');

    return `<h2>${titles.peer_review || 'Peer Review Process & Workflow Stages'}</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
${stagesHtml}

<h2>${titles.revision || 'Revision Process'}</h2>
<p>${data.revision_text || ''}</p>

<h2>${titles.confidentiality || 'Confidentiality Policy'}</h2>
<p>${data.confidentiality_text || ''}</p>

<h2>${titles.conflicts || 'Reviewer Conflicts of Interest'}</h2>
<p>${data.conflicts_text || ''}</p>

<h2>${titles.independence || 'Editorial Independence'}</h2>
<p>${data.independence_text || ''}</p>`;
  }

  if (pageKey === 'publication_ethics') {
    return `<h2>Publication Ethics & Malpractice Policy</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}

<h2>${titles.conflict_of_interest || 'Conflict of Interest Policy'}</h2>
<p>${data.conflict_of_interest || ''}</p>

<h2>${titles.research_ethics || 'Research Ethics & Approvals'}</h2>
<p>${data.research_ethics || ''}</p>

<h2>${titles.originality || 'Originality & Duplicate Submission Policy'}</h2>
<p>${data.originality || ''}</p>

<h2>${titles.plagiarism || 'Plagiarism & Similarity Policy'}</h2>
<p>${data.plagiarism || ''}</p>

<h2>${titles.corrections || 'Post-Publication Corrections (Errata / Corrigenda)'}</h2>
<p>${data.corrections || ''}</p>

<h2>${titles.retractions || 'Retraction Criteria & Policy'}</h2>
<p>${data.retractions || ''}</p>`;
  }

  if (pageKey === 'contact') {
    return `<h2>${titles.section_9_1 || 'Journal Editorial Office'}</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<p><strong>Editorial Email:</strong> ${data.journal_email || ''}</p>
<p><strong>Editorial Phone:</strong> ${data.journal_phone || ''}</p>
<p><strong>Campus Address:</strong> ${data.journal_address || ''}</p>
<p><strong>Office Working Hours:</strong> ${data.office_hours || ''}</p>

<h2>${titles.section_9_2 || 'Publisher & Institutional Contact'}</h2>
<p><strong>Publisher:</strong> ${data.publisher_name || ''}</p>
<p><strong>Publisher Email:</strong> ${data.publisher_email || ''}</p>
<p><strong>Publisher Phone:</strong> ${data.publisher_phone || ''}</p>
<p><strong>Publisher Address:</strong> ${data.publisher_address || ''}</p>`;
  }

  if (pageKey === 'editorial_board') {
    return `<h2>Editorial Board Overview</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<h2>${titles.board_policy || 'Editorial Board Appointment & Governance Policy'}</h2>
<p>${data.board_policy || ''}</p>
<h2>${titles.dheeraj_intro || 'Editor-in-Chief Leadership Statement'}</h2>
<p>${data.dheeraj_intro || ''}</p>`;
  }

  if (pageKey === 'archives') {
    return `<h2>Archives & Volume Structure</h2>
${data.overview ? `<p>${data.overview}</p>\n` : ''}
<h2>${titles.frequency || 'Publication Frequency & Volume Structure'}</h2>
<p>${data.frequency || ''}</p>
<h2>${titles.preservation || 'Digital Preservation & Crossref DOI Statement'}</h2>
<p>${data.preservation_statement || ''}</p>`;
  }

  return '';
};

const PageContentAdmin = ({ onSwitchTab }) => {
  const [selectedPage, setSelectedPage] = useState('about_us');
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState('single'); // 'single' | 'sections'
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPage(selectedPage);
  }, [selectedPage]);

  const loadPage = async (pageKey) => {
    setLoading(true);
    setError('');
    try {
      const data = await getPageContent(pageKey);
      const initial = data || DEFAULT_PAGE_CONTENTS[pageKey] || {};
      setFormData(initial);
      // Auto-choose edit mode based on whether full_content is set
      if (initial.full_content && initial.full_content.trim().length > 0) {
        setEditMode('single');
      } else {
        setEditMode('sections');
      }
    } catch (err) {
      setError(`Failed to load content for ${pageKey}`);
      setFormData(DEFAULT_PAGE_CONTENTS[pageKey] || {});
      setEditMode('sections');
    }
    setLoading(false);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTitleChange = (titleKey, value) => {
    setFormData((prev) => ({
      ...prev,
      titles: {
        ...(prev.titles || {}),
        [titleKey]: value,
      },
    }));
  };

  const handleResetTitle = (titleKey, defaultTitle) => {
    setFormData((prev) => ({
      ...prev,
      titles: {
        ...(prev.titles || {}),
        [titleKey]: defaultTitle,
      },
    }));
  };

  const handleGenerateFullText = () => {
    const merged = { ...(DEFAULT_PAGE_CONTENTS[selectedPage] || {}), ...formData };
    const generated = generateFullPageHtml(selectedPage, merged);
    setFormData((prev) => ({
      ...prev,
      full_content: generated,
    }));
    setEditMode('single');
    setSuccess('Generated single page text from current sections! You can now edit the full info continuously in one place.');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleClearFullText = () => {
    if (window.confirm('Clear the Single Page Full Text? The live website will revert to rendering the modular sections with your custom head titles.')) {
      setFormData((prev) => {
        const copy = { ...prev };
        delete copy.full_content;
        return copy;
      });
      setEditMode('sections');
      setSuccess('Full page text cleared. Live site will now display modular sections.');
      setTimeout(() => setSuccess(''), 4000);
    }
  };

  const insertHtmlSnippet = (openTag, closeTag) => {
    const textarea = document.getElementById('full-content-editor');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = formData.full_content || '';
    const selectedText = currentText.substring(start, end) || 'Sample text';
    const newText = currentText.substring(0, start) + openTag + selectedText + closeTag + currentText.substring(end);
    handleFieldChange('full_content', newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, start + openTag.length + selectedText.length);
    }, 50);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    try {
      await savePageContent(selectedPage, formData);
      const pageLabel = PAGE_DEFINITIONS.find((p) => p.key === selectedPage)?.label;
      setSuccess(`Content for "${pageLabel}" saved successfully! Live website updated instantly.`);
      setTimeout(() => setSuccess(''), 4500);
    } catch (err) {
      setError(err.message || 'Failed to save changes.');
    }
    setSaving(false);
  };

  const handleReset = async () => {
    const pageObj = PAGE_DEFINITIONS.find((p) => p.key === selectedPage);
    if (window.confirm(`Reset "${pageObj?.label}" to official default academic text? All custom titles and text will be restored to defaults.`)) {
      setSaving(true);
      try {
        const defaultData = DEFAULT_PAGE_CONTENTS[selectedPage] || {};
        await savePageContent(selectedPage, defaultData);
        setFormData(defaultData);
        setEditMode('sections');
        setSuccess(`Reset "${pageObj?.label}" to default content!`);
        setTimeout(() => setSuccess(''), 4000);
      } catch (err) {
        setError(err.message || 'Failed to reset content.');
      }
      setSaving(false);
    }
  };

  /**
   * Reusable component for rendering an editable Head Title input
   */
  const EditableHeadTitle = ({ titleKey, defaultTitle, label = 'Section Heading' }) => {
    const currentVal = formData?.titles && formData.titles[titleKey] !== undefined
      ? formData.titles[titleKey]
      : defaultTitle;

    const isCustomized = formData?.titles?.[titleKey] !== undefined && formData.titles[titleKey] !== defaultTitle;

    return (
      <div className="bg-amber-50/70 border border-amber-200/90 rounded-xl p-3 mb-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-[11px] font-bold text-amber-950 uppercase tracking-wider flex items-center gap-1.5">
            <span className="text-amber-600">🏷️</span> {label}
            <span className="font-normal text-amber-700/80 text-[10px] lowercase">(editable head title)</span>
          </label>
          {isCustomized && (
            <button
              type="button"
              onClick={() => handleResetTitle(titleKey, defaultTitle)}
              className="text-[10px] text-amber-800 hover:text-amber-950 font-semibold underline"
            >
              Reset to default
            </button>
          )}
        </div>
        <input
          type="text"
          value={currentVal}
          onChange={(e) => handleTitleChange(titleKey, e.target.value)}
          placeholder={defaultTitle}
          className="w-full text-xs sm:text-sm font-semibold p-2.5 border border-amber-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 shadow-xs"
        />
      </div>
    );
  };

  const isFullContentActive = Boolean(formData.full_content && formData.full_content.trim().length > 0);
  const isRawHtmlActive = isRawHtmlPage(formData);

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        
        {/* Header & Main Save Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Live Page Content Editor</h2>
            <p className="text-sm text-gray-500 mt-1">
              Choose single-page full text or flexible modular sections. Full control over all head titles and body information.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={saving || loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-xs font-semibold disabled:opacity-50"
            >
              Reset to Defaults
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-bold shadow-xs disabled:opacity-50 flex items-center gap-2"
            >
              <span>💾</span>
              <span>{saving ? 'Saving...' : 'Save Changes to Live Site'}</span>
            </button>
          </div>
        </div>

        {/* Page Switcher Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-100 pb-4">
          {PAGE_DEFINITIONS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setSelectedPage(p.key)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                selectedPage === p.key
                  ? 'bg-[#03478E] text-white shadow-xs'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
              <span className="block text-[10px] opacity-75 font-mono font-normal">
                {p.path}
              </span>
            </button>
          ))}
          {onSwitchTab && (
            <button
              type="button"
              onClick={() => onSwitchTab('legal')}
              className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-amber-50 text-amber-900 border border-amber-200/80 hover:bg-amber-100 flex flex-col items-start justify-center"
            >
              <span>📜 Legal &amp; Policies</span>
              <span className="block text-[10px] text-amber-700 font-mono font-normal">
                Privacy, Copyright, Terms, Disclaimer ↗
              </span>
            </button>
          )}
        </div>

        {/* Status Alerts */}
        {error && <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}
        {success && <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-sm font-medium">{success}</div>}

        {selectedPage === 'home_cards' ? (
          <div className="space-y-5">
            <p className="text-xs text-gray-600">
              The three cards on the home page, in order. Write the heading as plain text and the body as HTML
              (e.g. <code className="text-blue-900 font-bold">&lt;p&gt;</code>, <code className="text-blue-900 font-bold">&lt;strong&gt;</code>,{' '}
              <code className="text-blue-900 font-bold">&lt;br&gt;</code>, <code className="text-blue-900 font-bold">&lt;a href="..."&gt;</code>).
            </p>
            {HOME_CARDS.map(({ n, tag }) => (
              <div key={n} className="border border-gray-200 rounded-2xl p-5 bg-white grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-[#0B3B60]">
                    Card {n} <span className="text-xs font-semibold text-gray-500">({tag})</span>
                  </h3>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Heading</label>
                    <input
                      type="text"
                      value={formData[`card${n}_heading`] || ''}
                      onChange={(e) => handleFieldChange(`card${n}_heading`, e.target.value)}
                      className="w-full text-xs sm:text-sm font-semibold p-2.5 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Body (HTML)</label>
                    <textarea
                      rows={6}
                      value={formData[`card${n}_body`] || ''}
                      onChange={(e) => handleFieldChange(`card${n}_body`, e.target.value)}
                      className="w-full text-xs font-mono p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 mb-1">Preview</span>
                  <div className="p-6 bg-[#F8FAFC] rounded-3xl border border-gray-100">
                    {/* <span className="text-[10px] font-bold uppercase tracking-wider text-[#03478E] bg-blue-50 px-2.5 py-1 rounded-full inline-block mb-3">
                      {tag}
                    </span> */}
                    <h4 className="text-base font-serif font-bold text-[#0B3B60] mb-2.5">{formData[`card${n}_heading`]}</h4>
                    <div
                      className="text-xs text-gray-600 leading-relaxed space-y-2 [&_strong]:font-semibold [&_strong]:text-gray-700 [&_a]:text-[#03478E] [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                      dangerouslySetInnerHTML={{ __html: formData[`card${n}_body`] || '' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (<>
        {/* Mode Selector & Status Ribbon */}
        <div className="mb-6 bg-gradient-to-r from-gray-50 to-blue-50/40 border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">
              Select Editing Mode
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditMode('single')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                  editMode === 'single'
                    ? 'bg-[#03478E] text-white shadow-xs'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>📄 Single Page Full Text</span>
                {isFullContentActive && (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" title="Active on live site" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setEditMode('sections')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                  editMode === 'sections'
                    ? 'bg-[#03478E] text-white shadow-xs'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>📑 Flexible Sections &amp; Head Titles</span>
                {!isFullContentActive && (
                  <span className="w-2 h-2 rounded-full bg-blue-400" title="Active on live site" />
                )}
              </button>
            </div>
          </div>

          {/* Live Website Status Badge */}
          <div className="text-right">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-0.5">
              Current Live Status
            </span>
            {isRawHtmlActive ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-200">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                Simple HTML Page is Active on Live Site
              </span>
            ) : isFullContentActive ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Single Page Text is Active on Live Site
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Modular Sections (with Custom Head Titles) Active
              </span>
            )}
          </div>
        </div>

        {/* Simple HTML Toggle */}
        <div className="mb-6 bg-purple-50/60 border border-purple-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-sm font-bold text-purple-950 block">Simple HTML Mode</span>
            <p className="text-xs text-purple-900/80 mt-0.5 max-w-2xl leading-relaxed">
              When on, the live page shows only the HTML written in <strong>Single Page Full Text</strong>, rendered exactly as written:
              no page template, headings, cards or site styling. Turn off to go back to the normal page design.
            </p>
            {formData.use_raw_html && isFullContentActive && (() => {
              const sections = getHtmlSections(formData.full_content);
              return (
                <div className="text-xs text-purple-950 mt-2">
                  <span className="font-semibold">Sections (shown in the header menu and page sidebar):</span>{' '}
                  {sections.length > 0
                    ? sections.map((sec) => sec.label).join(' · ')
                    : <span className="italic">none yet. Wrap each section title in a heading, e.g. &lt;h2&gt;Revision Process&lt;/h2&gt;</span>}
                </div>
              );
            })()}
            {formData.use_raw_html && !isFullContentActive && (
              <p className="text-xs font-semibold text-red-700 mt-1">
                No HTML written yet, so the normal page is still shown. Add HTML in Single Page Full Text.
              </p>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(formData.use_raw_html)}
            onClick={() => {
              const next = !formData.use_raw_html;
              handleFieldChange('use_raw_html', next);
              if (next) setEditMode('single');
            }}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
              formData.use_raw_html ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <span className="sr-only">Toggle Simple HTML Mode</span>
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                formData.use_raw_html ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 font-medium animate-pulse">
            Loading page content...
          </div>
        ) : (
          <div className="space-y-6">

            {/* ========================================================================= */}
            {/* MODE 1: SINGLE PAGE FULL TEXT EDITOR                                      */}
            {/* ========================================================================= */}
            {editMode === 'single' && (
              <div className="space-y-5">
                <div className="bg-blue-50/70 border border-blue-200/80 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-[#0B3B60] flex items-center gap-2">
                      <span>📄</span> Single Page Full Info Document
                    </h3>
                    <p className="text-xs text-gray-600 mt-1 max-w-2xl leading-relaxed">
                      Write or edit the entire page content continuously in one place. No rigid accordion dividing or locked layouts. All standard HTML tags (<code className="text-blue-900 font-bold">&lt;h2&gt;</code>, <code className="text-blue-900 font-bold">&lt;h3&gt;</code>, <code className="text-blue-900 font-bold">&lt;p&gt;</code>, <code className="text-blue-900 font-bold">&lt;ul&gt;</code>, <code className="text-blue-900 font-bold">&lt;li&gt;</code>, <code className="text-blue-900 font-bold">&lt;strong&gt;</code>) are styled beautifully on the live website.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleGenerateFullText}
                      className="px-3.5 py-2 bg-white hover:bg-gray-50 border border-indigo-300 text-indigo-700 rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-colors"
                      title="Compile current section titles and text into this single document"
                    >
                      <span>✨</span> Auto-Generate from Sections
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 ${
                        showPreview
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                    >
                      <span>👁️</span> {showPreview ? 'Hide Preview' : 'Live Preview'}
                    </button>

                    {isFullContentActive && (
                      <button
                        type="button"
                        onClick={handleClearFullText}
                        className="px-3.5 py-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 rounded-xl text-xs font-bold transition-colors"
                        title="Delete single page text to revert back to individual sections"
                      >
                        🗑️ Revert to Sections
                      </button>
                    )}
                  </div>
                </div>

                {/* Page Title Input */}
                <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider mb-1.5">
                    Main Page Heading (H1)
                  </label>
                  <input
                    type="text"
                    value={formData.titles?.page_title || formData.page_title || ''}
                    onChange={(e) => handleTitleChange('page_title', e.target.value)}
                    placeholder={PAGE_DEFINITIONS.find((p) => p.key === selectedPage)?.label || 'Page Heading'}
                    className="w-full text-sm font-semibold p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Quick HTML Snippet Helper Toolbar */}
                <div className="bg-gray-100/80 p-2.5 rounded-xl border border-gray-200 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-[11px] font-bold text-gray-500 px-1 uppercase tracking-wider">Quick Insert:</span>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<h2>', '</h2>\n')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;h2&gt; Main Section
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<h3>', '</h3>\n')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;h3&gt; Subsection
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<p>', '</p>\n')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;p&gt; Paragraph
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<ul>\n  <li>', '</li>\n</ul>\n')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;ul&gt; Bullet List
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<strong>', '</strong>')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;strong&gt; Bold
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<a href="https://..." class="text-blue-600 underline">', '</a>')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + &lt;a&gt; Link
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHtmlSnippet('<div class="bg-blue-50/60 p-4 rounded-xl border border-blue-200 my-4">\n  <p>', '</p>\n</div>\n')}
                    className="px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-300 rounded font-semibold text-gray-700 text-[11px]"
                  >
                    + Callout Box
                  </button>
                </div>

                {/* Textarea Editor */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Single Page Full Text (HTML &amp; Plain Text)
                    </label>
                    <span className="text-[11px] text-gray-500 font-mono">
                      {(formData.full_content || '').length} characters
                    </span>
                  </div>
                  <textarea
                    id="full-content-editor"
                    rows={20}
                    value={formData.full_content || ''}
                    onChange={(e) => handleFieldChange('full_content', e.target.value)}
                    placeholder="Enter or paste the complete text of the page here. You can also click 'Auto-Generate from Sections' above to pre-populate with the current content."
                    className="w-full text-xs sm:text-sm font-mono p-4 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                  />
                </div>

                {/* Live Preview Panel */}
                {showPreview && (
                  <div className="border border-blue-200 rounded-2xl p-6 bg-white shadow-xs space-y-3">
                    <div className="flex items-center justify-between border-b pb-2">
                      <span className="text-xs font-bold text-[#03478E] uppercase tracking-wider flex items-center gap-1.5">
                        <span>👁️</span> Real-Time Render Preview
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {formData.use_raw_html ? 'Simple HTML: exactly as written' : 'Matches live website typography'}
                      </span>
                    </div>
                    {formData.use_raw_html ? (
                      <div
                        className="raw-html-content pt-2"
                        dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(formData.full_content) || '<p><i>No HTML entered yet.</i></p>' }}
                      />
                    ) : (
                      <div className="prose prose-blue max-w-none text-gray-800 leading-relaxed pt-2">
                        <HtmlContent
                          content={formData.full_content || '<p class="text-gray-400 italic">No text entered yet. Type above or click "Auto-Generate from Sections"!</p>'}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ========================================================================= */}
            {/* MODE 2: MODULAR SECTIONS WITH EDITABLE HEAD TITLES                         */}
            {/* ========================================================================= */}
            {editMode === 'sections' && (
              <div className="space-y-6">

                {/* Information Callout */}
                <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-950 flex items-start justify-between gap-4">
                  <div>
                    <span className="font-bold">Flexible Head Titles Enabled:</span> Every section heading below is now an editable field (highlighted with 🏷️). You can customize section numbering, rename policies, or rewrite headings to match your needs.
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateFullText}
                    className="flex-shrink-0 px-3 py-1.5 bg-white border border-amber-300 hover:bg-amber-100 rounded-lg font-bold text-amber-900 text-[11px]"
                  >
                    ✨ Merge into Single Page Text
                  </button>
                </div>

                {/* Main Page Title */}
                <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200 space-y-1.5">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Main Page Heading (H1)
                  </label>
                  <input
                    type="text"
                    value={formData.titles?.page_title || formData.page_title || ''}
                    onChange={(e) => handleTitleChange('page_title', e.target.value)}
                    placeholder={PAGE_DEFINITIONS.find((p) => p.key === selectedPage)?.label || 'Page Heading'}
                    className="w-full text-xs sm:text-sm font-semibold p-2.5 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                {/* Overview Intro */}
                <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200 space-y-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Overview Introduction (Displayed below title)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.overview || ''}
                    onChange={(e) => handleFieldChange('overview', e.target.value)}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* ======================= ABOUT US SECTIONS ======================= */}
                {selectedPage === 'about_us' && (
                  <div className="space-y-6">
                    {/* Section 2.1 */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="section_2_1"
                        defaultTitle="Mission, Vision & Scope"
                        label="Section Head Title"
                      />

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="mission"
                          defaultTitle="Mission Statement"
                          label="Mission Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.mission || ''}
                          onChange={(e) => handleFieldChange('mission', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="vision"
                          defaultTitle="Vision Statement"
                          label="Vision Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.vision || ''}
                          onChange={(e) => handleFieldChange('vision', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="scope"
                          defaultTitle="Scope Overview"
                          label="Scope Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.scope || ''}
                          onChange={(e) => handleFieldChange('scope', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="editorial_policy"
                          defaultTitle="Editorial Policy"
                          label="Editorial Policy Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.editorial_policy || ''}
                          onChange={(e) => handleFieldChange('editorial_policy', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="who_can_submit"
                          defaultTitle="Who Can Submit?"
                          label="Who Can Submit Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.who_can_submit || ''}
                          onChange={(e) => handleFieldChange('who_can_submit', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Section 2.2 */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="section_2_2"
                        defaultTitle="Journal Information & Publication History"
                        label="Journal Info Head Title"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <EditableHeadTitle
                            titleKey="journal_title"
                            defaultTitle="Journal Title"
                            label="Field Title"
                          />
                          <input
                            type="text"
                            value={formData.journal_title || ''}
                            onChange={(e) => handleFieldChange('journal_title', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <EditableHeadTitle
                            titleKey="journal_frequency"
                            defaultTitle="Publication Frequency"
                            label="Field Title"
                          />
                          <input
                            type="text"
                            value={formData.journal_frequency || ''}
                            onChange={(e) => handleFieldChange('journal_frequency', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <EditableHeadTitle
                            titleKey="journal_model"
                            defaultTitle="Publishing Model"
                            label="Field Title"
                          />
                          <input
                            type="text"
                            value={formData.journal_model || ''}
                            onChange={(e) => handleFieldChange('journal_model', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <EditableHeadTitle
                            titleKey="journal_history"
                            defaultTitle="Publication History"
                            label="Field Title"
                          />
                          <input
                            type="text"
                            value={formData.journal_history || ''}
                            onChange={(e) => handleFieldChange('journal_history', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2.3 */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="section_2_3"
                        defaultTitle="Publisher & Institutional Information"
                        label="Publisher Info Head Title"
                      />
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Publisher Legal Name</label>
                          <input
                            type="text"
                            value={formData.publisher_name || ''}
                            onChange={(e) => handleFieldChange('publisher_name', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Registered Address</label>
                          <input
                            type="text"
                            value={formData.publisher_address || ''}
                            onChange={(e) => handleFieldChange('publisher_address', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Institutional Affiliation &amp; Support</label>
                          <textarea
                            rows={2}
                            value={formData.publisher_affiliation || ''}
                            onChange={(e) => handleFieldChange('publisher_affiliation', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ======================= AIM & SCOPE SECTIONS ======================= */}
                {selectedPage === 'aims_and_scope' && (
                  <div className="space-y-6">
                    {/* Aim */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="aim"
                        defaultTitle="Principal Aim"
                        label="Aim Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.aim || ''}
                        onChange={(e) => handleFieldChange('aim', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Scope */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="scope"
                        defaultTitle="Scope & Subject Areas"
                        label="Scope Head Title"
                      />
                      <label className="block text-xs font-semibold text-gray-700">
                        Scope Points (one topic per line)
                      </label>
                      <textarea
                        rows={6}
                        value={Array.isArray(formData.scope_items) ? formData.scope_items.join('\n') : (formData.scope_items || '')}
                        onChange={(e) => handleFieldChange('scope_items', e.target.value.split('\n').filter(Boolean))}
                        placeholder="One scope topic per line"
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg font-sans"
                      />
                    </div>

                    {/* Geographical Scope */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="geographical_scope"
                        defaultTitle="Geographical Scope"
                        label="Geographical Scope Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.geographical_scope || ''}
                        onChange={(e) => handleFieldChange('geographical_scope', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Target Audience */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="target_audience"
                        defaultTitle="Target Audience"
                        label="Target Audience Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.target_audience || ''}
                        onChange={(e) => handleFieldChange('target_audience', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Editorial Scope */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="editorial_scope"
                        defaultTitle="Editorial Scope and Peer Review Policy"
                        label="Editorial Scope Head Title"
                      />
                      <textarea
                        rows={4}
                        value={formData.editorial_scope || ''}
                        onChange={(e) => handleFieldChange('editorial_scope', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Article Types */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="article_types"
                        defaultTitle="Article Types & Word Counts"
                        label="Article Types Head Title"
                      />
                      <p className="text-xs text-gray-500">
                        Article categories will render in the stylish card list on the live page.
                      </p>
                    </div>
                  </div>
                )}

                {/* ======================= PEER REVIEW SECTIONS ======================= */}
                {selectedPage === 'peer_review' && (
                  <div className="space-y-6">
                    {/* Workflow Stages */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="peer_review"
                        defaultTitle="Peer Review Process & Workflow Stages"
                        label="Peer Review Head Title"
                      />
                      <p className="text-xs text-gray-500">
                        Stages 1 to 5 are displayed as an open continuous workflow sequence on the live website.
                      </p>
                    </div>

                    {/* Revision Process */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="revision"
                        defaultTitle="Revision Process"
                        label="Revision Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.revision_text || ''}
                        onChange={(e) => handleFieldChange('revision_text', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Confidentiality */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="confidentiality"
                        defaultTitle="Confidentiality Policy"
                        label="Confidentiality Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.confidentiality_text || ''}
                        onChange={(e) => handleFieldChange('confidentiality_text', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Reviewer Conflicts */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="conflicts"
                        defaultTitle="Reviewer Conflicts of Interest"
                        label="Conflicts Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.conflicts_text || ''}
                        onChange={(e) => handleFieldChange('conflicts_text', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Editorial Independence */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="independence"
                        defaultTitle="Editorial Independence"
                        label="Independence Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.independence_text || ''}
                        onChange={(e) => handleFieldChange('independence_text', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* ======================= PUBLICATION ETHICS SECTIONS ======================= */}
                {selectedPage === 'publication_ethics' && (
                  <div className="space-y-6">
                    {/* Conflict of Interest */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="conflict_of_interest"
                        defaultTitle="Conflict of Interest Policy"
                        label="Conflict of Interest Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.conflict_of_interest || ''}
                        onChange={(e) => handleFieldChange('conflict_of_interest', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Research Ethics */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="research_ethics"
                        defaultTitle="Research Ethics & Approvals"
                        label="Research Ethics Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.research_ethics || ''}
                        onChange={(e) => handleFieldChange('research_ethics', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Originality */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="originality"
                        defaultTitle="Originality & Duplicate Submission Policy"
                        label="Originality Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.originality || ''}
                        onChange={(e) => handleFieldChange('originality', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Plagiarism */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="plagiarism"
                        defaultTitle="Plagiarism & Similarity Policy"
                        label="Plagiarism Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.plagiarism || ''}
                        onChange={(e) => handleFieldChange('plagiarism', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Corrections */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="corrections"
                        defaultTitle="Post-Publication Corrections (Errata / Corrigenda)"
                        label="Corrections Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.corrections || ''}
                        onChange={(e) => handleFieldChange('corrections', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Retractions */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="retractions"
                        defaultTitle="Retraction Criteria & Policy"
                        label="Retractions Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.retractions || ''}
                        onChange={(e) => handleFieldChange('retractions', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* ======================= AUTHOR GUIDELINES SECTIONS ======================= */}
                {selectedPage === 'author_guidelines' && (
                  <div className="space-y-6">
                    {/* Manuscript Preparation */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-5">
                      <EditableHeadTitle
                        titleKey="manuscript_preparation"
                        defaultTitle="Manuscript Preparation"
                        label="Preparation Section Head Title"
                      />

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_prep_overview"
                          defaultTitle="Overview Statement"
                          label="Overview Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.manuscript_preparation || ''}
                          onChange={(e) => handleFieldChange('manuscript_preparation', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_formatting"
                          defaultTitle="Formatting & Technical Specifications"
                          label="Formatting Head Title"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Font Standard</label>
                            <input
                              type="text"
                              value={formData.formatting_font || ''}
                              onChange={(e) => handleFieldChange('formatting_font', e.target.value)}
                              className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Spacing &amp; Margins</label>
                            <input
                              type="text"
                              value={formData.formatting_spacing || ''}
                              onChange={(e) => handleFieldChange('formatting_spacing', e.target.value)}
                              className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Remedy Nomenclature</label>
                            <input
                              type="text"
                              value={formData.formatting_remedies || ''}
                              onChange={(e) => handleFieldChange('formatting_remedies', e.target.value)}
                              className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_structure"
                          defaultTitle="Manuscript Structure (IMRAD)"
                          label="Structure Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.structure || ''}
                          onChange={(e) => handleFieldChange('structure', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_conflict"
                          defaultTitle="Conflict of Interest Declaration"
                          label="Conflict Head Title"
                        />
                        <textarea
                          rows={3}
                          value={formData.conflict_of_interest || ''}
                          onChange={(e) => handleFieldChange('conflict_of_interest', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_funding"
                          defaultTitle="Funding Sources"
                          label="Funding Head Title"
                        />
                        <textarea
                          rows={2}
                          value={formData.funding || ''}
                          onChange={(e) => handleFieldChange('funding', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_originality"
                          defaultTitle="Originality & Duplicate Submission Policy"
                          label="Originality Head Title"
                        />
                        <textarea
                          rows={2}
                          value={formData.originality || ''}
                          onChange={(e) => handleFieldChange('originality', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_ethics"
                          defaultTitle="Ethical Approval & Patient Consent"
                          label="Ethics Head Title"
                        />
                        <textarea
                          rows={2}
                          value={formData.ethics_and_consent || ''}
                          onChange={(e) => handleFieldChange('ethics_and_consent', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_figures"
                          defaultTitle="Tables, Figures & Language Standards"
                          label="Figures & Language Head Title"
                        />
                        <textarea
                          rows={2}
                          value={formData.tables_figures || ''}
                          onChange={(e) => handleFieldChange('tables_figures', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <EditableHeadTitle
                          titleKey="sub_responsibility"
                          defaultTitle="Author Responsibility"
                          label="Responsibility Head Title"
                        />
                        <textarea
                          rows={2}
                          value={formData.author_responsibility || ''}
                          onChange={(e) => handleFieldChange('author_responsibility', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Submission Process */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="submission_process"
                        defaultTitle="Submission Process"
                        label="Submission Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.submission_process || ''}
                        onChange={(e) => handleFieldChange('submission_process', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {/* Article Types */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="article_types"
                        defaultTitle="Article Types & Word Limits"
                        label="Article Types Head Title"
                      />
                      <p className="text-xs text-gray-500">
                        Article categories will render in a dedicated table format on the live website.
                      </p>
                    </div>

                    {/* Article Processing Charges */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="apc"
                        defaultTitle="Article Processing Charges (APC)"
                        label="APC Head Title"
                      />
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700">APC Policy Overview</label>
                        <textarea
                          rows={2}
                          value={formData.apc_overview || ''}
                          onChange={(e) => handleFieldChange('apc_overview', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700">APC Fee Schedule</label>
                        <input
                          type="text"
                          value={formData.apc_fee || ''}
                          onChange={(e) => handleFieldChange('apc_fee', e.target.value)}
                          className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700">Waiver Policy</label>
                        <textarea
                          rows={2}
                          value={formData.waiver_policy || ''}
                          onChange={(e) => handleFieldChange('waiver_policy', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ======================= CONTACT INFORMATION SECTIONS ======================= */}
                {selectedPage === 'contact' && (
                  <div className="space-y-6">
                    {/* Editorial Contact */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="section_9_1"
                        defaultTitle="Journal Editorial Office"
                        label="Editorial Office Head Title"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Editorial Email(s)</label>
                          <input
                            type="text"
                            value={formData.journal_email || ''}
                            onChange={(e) => handleFieldChange('journal_email', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Editorial Phone Number(s)</label>
                          <input
                            type="text"
                            value={formData.journal_phone || ''}
                            onChange={(e) => handleFieldChange('journal_phone', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Campus Physical Address</label>
                        <textarea
                          rows={2}
                          value={formData.journal_address || ''}
                          onChange={(e) => handleFieldChange('journal_address', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Office Working Hours</label>
                        <input
                          type="text"
                          value={formData.office_hours || ''}
                          onChange={(e) => handleFieldChange('office_hours', e.target.value)}
                          className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Publisher Contact */}
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-4">
                      <EditableHeadTitle
                        titleKey="section_9_2"
                        defaultTitle="Publisher & Institutional Contact"
                        label="Publisher Contact Head Title"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Publisher Legal Name</label>
                          <input
                            type="text"
                            value={formData.publisher_name || ''}
                            onChange={(e) => handleFieldChange('publisher_name', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Publisher Email</label>
                          <input
                            type="text"
                            value={formData.publisher_email || ''}
                            onChange={(e) => handleFieldChange('publisher_email', e.target.value)}
                            className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Publisher Physical Address</label>
                        <textarea
                          rows={2}
                          value={formData.publisher_address || ''}
                          onChange={(e) => handleFieldChange('publisher_address', e.target.value)}
                          className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Publisher Phone</label>
                        <input
                          type="text"
                          value={formData.publisher_phone || ''}
                          onChange={(e) => handleFieldChange('publisher_phone', e.target.value)}
                          className="w-full text-xs p-2.5 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ======================= EDITORIAL BOARD SECTIONS ======================= */}
                {selectedPage === 'editorial_board' && (
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="board_policy"
                        defaultTitle="Editorial Board Appointment & Governance Policy"
                        label="Policy Head Title"
                      />
                      <textarea
                        rows={4}
                        value={formData.board_policy || ''}
                        onChange={(e) => handleFieldChange('board_policy', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="dheeraj_intro"
                        defaultTitle="Editor-in-Chief Leadership Statement (Dr. Dheeraj Pandey)"
                        label="Statement Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.dheeraj_intro || ''}
                        onChange={(e) => handleFieldChange('dheeraj_intro', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* ======================= ARCHIVES SECTIONS ======================= */}
                {selectedPage === 'archives' && (
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="frequency"
                        defaultTitle="Publication Frequency & Volume Structure"
                        label="Frequency Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.frequency || ''}
                        onChange={(e) => handleFieldChange('frequency', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div className="border border-gray-200 rounded-2xl p-5 bg-white space-y-3">
                      <EditableHeadTitle
                        titleKey="preservation"
                        defaultTitle="Digital Preservation & Crossref DOI Statement"
                        label="Preservation Head Title"
                      />
                      <textarea
                        rows={3}
                        value={formData.preservation_statement || ''}
                        onChange={(e) => handleFieldChange('preservation_statement', e.target.value)}
                        className="w-full text-xs p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Bottom Save Action Button */}
            <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-gray-500">
                {editMode === 'single'
                  ? 'Saving in Single Page Mode will render this unified text document on the live website.'
                  : 'Saving in Sections Mode will render the modular sections with your custom head titles.'}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving || loading}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl text-xs font-semibold disabled:opacity-50"
                >
                  Reset Defaults
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-xs disabled:opacity-50 flex items-center gap-2"
                >
                  <span>💾</span>
                  <span>{saving ? 'Saving...' : 'Save Changes to Live Site'}</span>
                </button>
              </div>
            </div>

          </div>
        )}
        </>)}

      </div>
    </div>
  );
};

export default PageContentAdmin;
